import React from 'react';
import { Icon, Dropdown, Button, Menu, MenuItem } from 'components';
import styled from 'styled-components';
import RenameModal from './RenameModal';
import UploadModal from '../UploadModal';
import DocumentsContext from '../DocumentsContext';
import { DocumentType } from '../types';
import ConfirmArchive from './ConfirmArchive';
import ConfirmReplace from './ConfirmReplace';

type ManageDropdownProps = Pick<
  DocumentType,
  'id' | 'isDeleted' | 'name' | 'documentType'
>;

type MenuItemType =
  | 'archive'
  | 'restore'
  | 'rename'
  | 'replace'
  | 'print'
  | 'view-history';

type MenuItemEntry = {
  id: MenuItemType;
  element: JSX.Element;
};

const MENU_PADDING = 10;
const MENU_ITEM_HEIGHT = 48;

const ManageDropdown: React.FC<ManageDropdownProps> = ({
  id,
  isDeleted,
  name,
  documentType,
}) => {
  const { setHistoryOpen, restore, print } = React.useContext(DocumentsContext);

  const [dropdownVisible, setDropdownVisible] = React.useState(false);
  const [renameModalOpen, setRenameModalOpen] = React.useState(false);
  const [replaceModalOpen, setReplaceModalOpen] = React.useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = React.useState(false);
  const [replaceConfirmOpen, setReplaceConfirmOpen] = React.useState(false);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = React.useState(false);

  const getReplaceOnClick = () => {
    if (documentType === 'listingAgreement') {
      if (!replaceConfirmOpen) {
        return () => setReplaceConfirmOpen(true);
      }
      return null;
    }
    return () => {
      setDropdownVisible(false);
      setReplaceModalOpen(true);
    };
  };

  const getRestoreOnClick = () => {
    if (documentType === 'listingAgreement') {
      if (!restoreConfirmOpen) {
        return () => setRestoreConfirmOpen(true);
      }
      return null;
    }
    return () => {
      restore(id);
    };
  };

  const ViewHistoryItem: MenuItemEntry = {
    id: 'view-history',
    element: (
      <MenuItem
        key={'history'}
        icon={<Icon type="history" />}
        onClick={() => setHistoryOpen(id)}
      >
        View History
      </MenuItem>
    ),
  };

  const RestoreItem: MenuItemEntry = {
    id: 'restore',
    element: (
      <MenuItem
        key={'restore'}
        icon={<Icon type="restore" />}
        onClick={getRestoreOnClick()}
      >
        Restore
      </MenuItem>
    ),
  };

  const ReplaceItem: MenuItemEntry = {
    id: 'replace',
    element: (
      <MenuItem
        key={'replace'}
        icon={<Icon type="duplicate" />}
        onClick={getReplaceOnClick()}
      >
        Replace
      </MenuItem>
    ),
  };

  const RenameItem: MenuItemEntry = {
    id: 'rename',
    element: (
      <MenuItem
        key={'rename'}
        icon={<Icon type="antdEditFilled" />}
        onClick={() => setRenameModalOpen(true)}
      >
        Rename
      </MenuItem>
    ),
  };

  const ArchiveItem: MenuItemEntry = {
    id: 'archive',
    element: (
      <MenuItem
        key={'archive'}
        icon={<Icon type="archive" />}
        onClick={
          !archiveConfirmOpen ? () => setArchiveConfirmOpen(true) : null!
        }
      >
        Archive
      </MenuItem>
    ),
  };

  const PrintItem: MenuItemEntry = {
    id: 'print',
    element: (
      <MenuItem
        key={'print'}
        icon={<Icon type="antdPrinterFilled" />}
        onClick={() => print(id)}
      >
        Print
      </MenuItem>
    ),
  };

  const MENU_ITEMS: {
    index: number;
    id: MenuItemType;
    element: JSX.Element;
  }[] = [];

  const _addToMenu = (item: MenuItemEntry) =>
    MENU_ITEMS.push({ index: MENU_ITEMS.length, ...item });

  if (isDeleted) {
    _addToMenu(RestoreItem);
    _addToMenu(ViewHistoryItem);
  } else {
    _addToMenu(ReplaceItem);
    _addToMenu(RenameItem);
    if (documentType !== 'listingAgreement') _addToMenu(ArchiveItem);
    _addToMenu(PrintItem);
    _addToMenu(ViewHistoryItem);
  }

  const replaceIndex = MENU_ITEMS.findIndex(
    ({ id: itemId }) => itemId === 'replace',
  );
  const archiveIndex = MENU_ITEMS.findIndex(
    ({ id: itemId }) => itemId === 'archive',
  );
  const restoreIndex = MENU_ITEMS.findIndex(
    ({ id: itemId }) => itemId === 'restore',
  );

  const _getConfirmTooltipYOffset = (index: number) => -(index * MENU_ITEM_HEIGHT + MENU_ITEM_HEIGHT / 2 + MENU_PADDING);

  return (
    <>
      <Dropdown
        open={archiveConfirmOpen || dropdownVisible}
        onOpenChange={setDropdownVisible}
        dropdownRender={() => (
          <>
            {replaceIndex !== -1 && (
              <ConfirmReplace
                open={replaceConfirmOpen}
                onOpenChange={setReplaceConfirmOpen}
                handleConfirm={() => {
                  setDropdownVisible(false);
                  setReplaceModalOpen(true);
                  setReplaceConfirmOpen(false);
                }}
                align={{
                  targetOffset: [0, _getConfirmTooltipYOffset(replaceIndex)],
                }}
              />
            )}
            {archiveIndex !== -1 && (
              <ConfirmArchive
                id={id}
                open={archiveConfirmOpen}
                onOpenChange={setArchiveConfirmOpen}
                align={{
                  targetOffset: [0, _getConfirmTooltipYOffset(archiveIndex)],
                }}
              />
            )}
            {restoreIndex !== -1 && (
              <ConfirmReplace
                open={restoreConfirmOpen}
                onOpenChange={setRestoreConfirmOpen}
                handleConfirm={() => {
                  setDropdownVisible(false);
                  setRestoreConfirmOpen(false);
                  restore(id);
                }}
                align={{
                  targetOffset: [0, _getConfirmTooltipYOffset(restoreIndex)],
                }}
                isRestore
              />
            )}
            <Menu>{MENU_ITEMS.map(({ element }) => element)}</Menu>
          </>
        )}
      >
        <Button shape="circle" type="text">
          <Icon type="more" width={25} offset={[0, -1]} />
        </Button>
      </Dropdown>
      {renameModalOpen && (
        <RenameModal
          id={id}
          name={name}
          setRenameModalOpen={setRenameModalOpen}
        />
      )}
      {replaceModalOpen && (
        <UploadModal
          title="Replace Document"
          visible={replaceModalOpen}
          onCancel={() => setReplaceModalOpen(false)}
          replacingId={id}
          documentType={documentType}
        />
      )}
    </>
  );
};

export default ManageDropdown;
